import { useEffect, useState } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import FuseLoading from '@fuse/core/FuseLoading';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import { fetchFeaturedProducts, updateFeaturedProduct, type FeaturedAdminRow } from '@/lib/api';

const Root = styled(FusePageSimple)(({ theme }) => ({
	'& .FusePageSimple-header': {
		backgroundColor: theme.vars.palette.background.paper,
		borderBottomWidth: 1,
		borderStyle: 'solid',
		borderColor: theme.vars.palette.divider
	}
}));

/**
 * The hand-picked products on the home page and in the buying guides.
 *
 * Read-mostly by design. The products themselves live in the storefront's code
 * (`lib/catalog/featured.ts`) because production has no catalog rows and no
 * seeding path — adding or removing one is a pull request, not a form. What can
 * be changed here is the runtime state that must not require a deploy: whether
 * a pick is shown, and which tracking id it carries.
 */
function FeaturedView() {
	const [rows, setRows] = useState<FeaturedAdminRow[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [toast, setToast] = useState<string | null>(null);
	const [saving, setSaving] = useState<string | null>(null);

	useEffect(() => {
		fetchFeaturedProducts()
			.then(setRows)
			.catch((err) => setError(err.message));
	}, []);

	async function save(asin: string, patch: { enabled?: boolean; associateTag?: string | null }) {
		setSaving(asin);
		setError(null);

		try {
			const updated = await updateFeaturedProduct({ asin, ...patch });
			setRows(
				(prev) =>
					prev?.map((r) =>
						r.asin === asin
							? { ...r, enabled: updated.enabled, associateTag: updated.associateTag, hasOverride: true }
							: r
					) ?? null
			);
			setToast('Saved');
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setSaving(null);
		}
	}

	if (error && !rows) {
		return (
			<Root
				header={
					<div className="p-6">
						<Typography className="text-2xl font-bold">Featured products</Typography>
					</div>
				}
				content={
					<div className="p-6">
						<Alert severity="error">{error}</Alert>
					</div>
				}
			/>
		);
	}

	if (!rows) return <FuseLoading />;

	const hiddenCount = rows.filter((r) => !r.enabled).length;

	return (
		<Root
			header={
				<div className="p-6">
					<Typography className="text-2xl font-bold">Featured products</Typography>
					<Typography
						color="text.secondary"
						className="text-sm"
					>
						The hand-picked list on the home page and in the buying guides — {rows.length} product
						{rows.length === 1 ? '' : 's'}
						{hiddenCount > 0 ? `, ${hiddenCount} hidden` : ''}
					</Typography>
				</div>
			}
			content={
				<div className="p-6 flex flex-col gap-4 max-w-4xl">
					{error && <Alert severity="error">{error}</Alert>}

					<Alert severity="info">
						Adding or removing a product is a code change — the list lives in the
						storefront repo. Here you can hide one, or give it its own Associates
						tracking id so its sales are attributed separately. Both apply
						everywhere the product appears: hiding one also drops it from any
						buying guide that recommends it.
					</Alert>

					{rows.length === 0 && (
						<Paper className="p-5">
							<Typography color="text.secondary">
								No featured products are defined yet.
							</Typography>
						</Paper>
					)}

					{rows.map((r) => (
						<Paper
							key={r.asin}
							className="p-5"
							sx={{
								borderWidth: 1,
								borderStyle: 'solid',
								borderColor: r.enabled ? 'divider' : 'warning.main'
							}}
						>
							<div className="flex items-start justify-between gap-4">
								<div className="min-w-0">
									<Typography className="font-semibold">{r.title}</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										className="font-mono text-xs"
									>
										{r.asin}
										{r.brand ? ` · ${r.brand}` : ''} · {r.categoryId}
									</Typography>
								</div>
								<div className="flex items-center gap-2 shrink-0">
									{!r.enabled && (
										<Chip
											label="Hidden"
											size="small"
											color="warning"
										/>
									)}
									<Switch
										checked={r.enabled}
										disabled={saving === r.asin}
										onChange={(e) => save(r.asin, { enabled: e.target.checked })}
									/>
								</div>
							</div>

							<TextField
								label="Tracking id override"
								placeholder="(uses the site default)"
								size="small"
								fullWidth
								className="mt-4"
								defaultValue={r.associateTag ?? ''}
								disabled={saving === r.asin}
								// Saved on blur rather than per keystroke: each save is a
								// request, and a half-typed tag is not one worth storing.
								onBlur={(e) => {
									const next = e.target.value.trim();
									if (next !== (r.associateTag ?? '')) {
										save(r.asin, { associateTag: next || null });
									}
								}}
							/>
						</Paper>
					))}

					<Snackbar
						open={!!toast}
						autoHideDuration={2500}
						onClose={() => setToast(null)}
						message={toast ?? ''}
					/>
				</div>
			}
		/>
	);
}

export default FeaturedView;
